import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Mail, Key, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [activeTab, setActiveTab] = useState('login'); // login, admin
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code, 3: new password
  const [formData, setFormData] = useState({
    email: '',
    senha: '',
    nome: '',
    telefone: '',
    role: 'patient',
    altura: '',
    peso_inicial: '',
    peso_meta: ''
  });
  const [resetData, setResetData] = useState({
    email: '',
    code: '',
    nova_senha: '',
    confirmar_senha: ''
  });
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = () => {
    setShowPasswordReset(true);
    setResetStep(1);
    setResetData({ email: '', code: '', nova_senha: '', confirmar_senha: '' });
  };

  const handleBackToLogin = () => {
    setShowPasswordReset(false);
    setResetStep(1);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!resetData.email) {
      toast.error('Por favor, informe seu email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/request`, {
        email: resetData.email
      });
      toast.success('Se o email estiver cadastrado, você receberá um código de recuperação.');
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar recuperação');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!resetData.code || resetData.code.length !== 6) {
      toast.error('Por favor, informe o código de 6 dígitos');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/verify`, {
        email: resetData.email,
        code: resetData.code
      });
      toast.success('Código verificado! Agora defina sua nova senha.');
      setResetStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteReset = async (e) => {
    e.preventDefault();
    if (resetData.nova_senha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (resetData.nova_senha !== resetData.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/complete`, {
        email: resetData.email,
        code: resetData.code,
        nova_senha: resetData.nova_senha
      });
      toast.success('Senha redefinida com sucesso! Faça login com sua nova senha.');
      setShowPasswordReset(false);
      setResetStep(1);
      setFormData({ ...formData, email: resetData.email, senha: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (activeTab === 'login' || activeTab === 'admin') {
        const user = await login(formData.email, formData.senha);
        toast.success('Login realizado com sucesso!');
        
        if (user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (user.role === 'doctor') {
          navigate('/medico/dashboard');
        } else {
          navigate('/');
        }
      } else {
        const registerData = {
          ...formData,
          altura: formData.altura ? parseFloat(formData.altura) : null,
          peso_inicial: formData.peso_inicial ? parseFloat(formData.peso_inicial) : null,
          peso_meta: formData.peso_meta ? parseFloat(formData.peso_meta) : null
        };
        const user = await register(registerData);
        toast.success('Cadastro realizado com sucesso!');
        
        if (user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (user.role === 'doctor') {
          navigate('/medico/dashboard');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar solicitação');
    } finally {
      setLoading(false);
    }
  };

  // Password Reset Form
  const renderPasswordResetForm = () => (
    <div className="login-glass-card p-5 md:p-8 mx-1 md:mx-0">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handleBackToLogin}
          className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
          data-testid="back-to-login-btn"
        >
          <ArrowLeft size={20} className="text-muted-foreground" />
        </button>
        <div>
          <h2 className="text-lg md:text-xl font-bold text-highlight">Recuperar Senha</h2>
          <p className="text-xs md:text-sm text-muted-foreground">
            {resetStep === 1 && 'Informe seu email para receber o código'}
            {resetStep === 2 && 'Digite o código recebido por email'}
            {resetStep === 3 && 'Defina sua nova senha'}
          </p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
              step === resetStep 
                ? 'bg-[#0F2B4C] text-white' 
                : step < resetStep 
                  ? 'bg-green-500 text-white' 
                  : 'bg-muted text-muted-foreground'
            }`}>
              {step < resetStep ? '✓' : step}
            </div>
            {step < 3 && (
              <div className={`w-8 h-0.5 ${step < resetStep ? 'bg-green-500' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Email */}
      {resetStep === 1 && (
        <form onSubmit={handleRequestOTP} className="space-y-4">
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              Email cadastrado
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="reset-email-input"
                type="email"
                value={resetData.email}
                onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                className="w-full bg-muted/50 border-transparent focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/20 rounded-xl h-12 pl-10 pr-4 text-sm transition-all"
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>
          
          <button
            data-testid="request-otp-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl py-3 font-semibold transition-all disabled:opacity-50 hover:shadow-lg"
          >
            {loading ? 'Enviando...' : 'Enviar Código'}
          </button>
        </form>
      )}

      {/* Step 2: OTP Code */}
      {resetStep === 2 && (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              Código de 6 dígitos
            </label>
            <div className="relative">
              <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="otp-code-input"
                type="text"
                maxLength={6}
                value={resetData.code}
                onChange={(e) => setResetData({ ...resetData, code: e.target.value.replace(/\D/g, '') })}
                className="w-full bg-muted/50 border-transparent focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/20 rounded-xl h-12 pl-10 pr-4 text-sm tracking-[0.5em] text-center font-mono transition-all"
                placeholder="000000"
                required
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Verifique sua caixa de entrada e spam
            </p>
          </div>
          
          <button
            data-testid="verify-otp-btn"
            type="submit"
            disabled={loading || resetData.code.length !== 6}
            className="w-full bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl py-3 font-semibold transition-all disabled:opacity-50 hover:shadow-lg"
          >
            {loading ? 'Verificando...' : 'Verificar Código'}
          </button>
          
          <button
            type="button"
            onClick={() => setResetStep(1)}
            className="w-full text-sm text-muted-foreground hover:text-[#0F2B4C] transition-colors"
          >
            Não recebeu? Solicitar novamente
          </button>
        </form>
      )}

      {/* Step 3: New Password */}
      {resetStep === 3 && (
        <form onSubmit={handleCompleteReset} className="space-y-4">
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              Nova senha
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="new-password-input"
                type="password"
                value={resetData.nova_senha}
                onChange={(e) => setResetData({ ...resetData, nova_senha: e.target.value })}
                className="w-full bg-muted/50 border-transparent focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/20 rounded-xl h-12 pl-10 pr-4 text-sm transition-all"
                placeholder="Mínimo 6 caracteres"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              Confirmar nova senha
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="confirm-password-input"
                type="password"
                value={resetData.confirmar_senha}
                onChange={(e) => setResetData({ ...resetData, confirmar_senha: e.target.value })}
                className="w-full bg-muted/50 border-transparent focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/20 rounded-xl h-12 pl-10 pr-4 text-sm transition-all"
                placeholder="Digite novamente"
                required
              />
            </div>
          </div>
          
          <button
            data-testid="complete-reset-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl py-3 font-semibold transition-all disabled:opacity-50 hover:shadow-lg"
          >
            {loading ? 'Salvando...' : 'Redefinir Senha'}
          </button>
        </form>
      )}
    </div>
  );

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-8">
      {/* Background for Desktop */}
      <div className="hidden md:block absolute inset-0 bg-gradient-to-br from-[#f0f2f5] via-[#f5f7fa] to-[#eef1f5]"></div>
      
      {/* Frosted background for Mobile */}
      <div className="md:hidden absolute inset-0 overflow-hidden">
        <div 
          className="absolute -inset-4 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_weightgoals-app-1/artifacts/pjcg6jq4_doutor%20montafgem.png')`,
            filter: 'blur(2px) saturate(0.7)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#eef1f6]/55 via-[#f3f5f9]/45 to-[#eef1f6]/65" />
        <svg className="absolute inset-0 w-full h-full opacity-60" viewBox="0 0 390 844" fill="none" preserveAspectRatio="none">
          <path d="M-40 420 C 60 300, 120 180, 90 -20" stroke="#B8977A" strokeWidth="1" />
          <path d="M-20 520 C 120 380, 200 240, 170 -20" stroke="#B8977A" strokeWidth="0.6" />
          <path d="M420 480 C 330 560, 300 700, 360 880" stroke="#B8977A" strokeWidth="1" />
          <path d="M440 560 C 360 640, 340 760, 400 900" stroke="#B8977A" strokeWidth="0.6" />
        </svg>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-6 md:mb-8">
          <img 
            src="/logo-wp.webp" 
            alt="Logo" 
            className="h-28 md:h-24 w-auto object-contain mx-auto mb-4 drop-shadow-sm"
          />
          <p className="text-sm md:text-base text-[#0F2B4C]/80">
            Seu parceiro no emagrecimento saudável
          </p>
        </div>

        {/* Show Password Reset or Login Form */}
        {showPasswordReset ? (
          renderPasswordResetForm()
        ) : (
          <div className="login-glass-card p-5 md:p-8 mx-1 md:mx-0">
            <div className="grid grid-cols-2 gap-1 p-1 mb-5 md:mb-6 rounded-full bg-white/70 border border-[#0F2B4C]/10 shadow-inner">
              <button
                data-testid="tab-login"
                onClick={() => setActiveTab('login')}
                className={`py-2.5 md:py-3 rounded-full font-medium transition-all text-sm md:text-base ${
                  activeTab === 'login'
                    ? 'bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white shadow-lg shadow-[#0F2B4C]/30'
                    : 'text-muted-foreground hover:text-[#0F2B4C]'
                }`}
              >
                Entrar
              </button>
              <button
                data-testid="tab-admin"
                onClick={() => setActiveTab('admin')}
                className={`py-2.5 md:py-3 rounded-full font-medium transition-all text-sm md:text-base ${
                  activeTab === 'admin'
                    ? 'bg-gradient-to-r from-[#B8977A] to-[#9C7D5E] text-white shadow-lg shadow-[#B8977A]/30'
                    : 'text-muted-foreground hover:text-[#0F2B4C]'
                }`}
              >
                Admin
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#0F2B4C]">
                  {activeTab === 'admin' ? 'Login' : 'Email'}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <input
                    data-testid="input-email"
                    type={activeTab === 'admin' ? 'text' : 'email'}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-white/60 border border-[#0F2B4C]/10 focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/15 rounded-2xl h-12 pl-11 pr-4 text-sm transition-all placeholder:text-muted-foreground/70"
                    placeholder={activeTab === 'admin' ? 'whandersonpinheiro' : 'Seu email'}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 text-[#0F2B4C]">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <input
                    data-testid="input-senha"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.senha}
                    onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                    className="w-full bg-white/60 border border-[#0F2B4C]/10 focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/15 rounded-2xl h-12 pl-11 pr-12 text-sm transition-all placeholder:text-muted-foreground/70"
                    placeholder="Sua senha"
                    required
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0F2B4C] transition-colors"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Stay logged in checkbox and forgot password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="checkbox-stay-logged"
                    type="checkbox"
                    checked={stayLoggedIn}
                    onChange={(e) => setStayLoggedIn(e.target.checked)}
                    className="w-5 h-5 rounded-md border-gray-300 text-[#0F2B4C] focus:ring-[#0F2B4C]/20 cursor-pointer accent-[#0F2B4C]"
                  />
                  <span className="text-sm text-muted-foreground">Ficar logado</span>
                </label>
                <button
                  type="button"
                  data-testid="forgot-password-btn"
                  onClick={handleForgotPassword}
                  className="text-sm text-[#0F2B4C] font-medium hover:text-[#1C3D66] hover:underline transition-all"
                >
                  Esqueci a senha
                </button>
              </div>

              <button
                data-testid="submit-button"
                type="submit"
                disabled={loading}
                className={`w-full rounded-2xl px-6 md:px-8 py-3.5 md:py-4 font-semibold transition-all disabled:opacity-50 text-base flex items-center justify-center gap-2 active:scale-[0.98] ${
                  activeTab === 'admin' 
                    ? 'bg-gradient-to-r from-[#B8977A] to-[#9C7D5E] text-white shadow-lg shadow-[#B8977A]/30 hover:shadow-xl hover:shadow-[#B8977A]/40' 
                    : 'bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white shadow-lg shadow-[#0F2B4C]/30 hover:shadow-xl hover:shadow-[#0F2B4C]/40'
                }`}
              >
                {loading ? 'Processando...' : activeTab === 'admin' ? 'Entrar como Admin' : 'Entrar'}
                {!loading && <ArrowRight size={20} />}
              </button>
            </form>
          </div>
        )}

        {/* Footer with copyright */}
        <div className="text-center mt-6 md:mt-8">
          <p className="text-xs text-muted-foreground">
            © 2026 Doutor Whanderson Pinheiro. Todos os direitos reservados.
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            versão 2.0.1
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
